import { isEmpty, kebabCase } from 'lodash';
import { DateTime } from 'luxon';

import { Control, ControlConfig } from 'dashboard/components/FormControlPilledInput/types';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import {
  DatumType,
  DatumValueType,
  Edge,
  GraphPropertyType,
  HashableNodeId,
  Node,
  NodeId,
  nodeIdToHashable,
  PropertyConfig,
  V2Data,
} from 'dashboard/models/graph';
import {
  convertServerEdgeToEdge,
  convertServerNodeToNode,
  convertServerNodeToNodeId,
  ServerAccountsResponse,
  ServerClusterResponse,
  ServerNHopsResponse,
  ServerQueryCursor,
} from 'dashboard/models/graph-backend';
import { NodeRenderConfigLookupFn } from 'dashboard/models/graph-explorer';
import {
  ACCOUNT_DATUM_TYPE,
  ACCOUNT_REFERENCE_ID_SEARCH_PLACEHOLDER,
  ACCOUNT_STATUS_DATUM_TYPE,
  BIRTHDATE_DATUM_TYPE,
  BROWSER_FINGERPRINT_DATUM_TYPE,
  DEFAULT_ACCOUNT_DATUM_TYPE_RENDER_CONFIG,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  GOVERNMENT_ID_DATUM_TYPE,
  IP_ADDRESS_NAME,
  NAME_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PHONE_NUMBER_DATUM_TYPE,
  POSTAL_CODE_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_DATUM_TYPE,
  TRANSACTION_ID_SEARCH_PLACEHOLDER,
} from 'lib/constants/graph';
import { ApiModel, ApiOptionalRelationship, ApiResource } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';

export enum GraphQueryHintType {
  ReduceTimeRange = 'reduce_timerange',
}

export interface Creator {
  id: string;
  type: 'Account' | 'Case' | 'User';
}

export interface GraphQueryHint {
  type: GraphQueryHintType;
}

export type GraphQueryParams =
  | AccountsQuery
  | ClustersQuery
  | NHopsFromAccountQuery
  | ExperimentalLiveQuery;

export enum GraphQueryStatus {
  Submitted = 'submitted',
  Completed = 'completed',
  Errored = 'errored',
  TimedOut = 'timed_out',
}

export interface GraphQueryAttributes {
  status: GraphQueryStatus;
  'cancelled-at': string;
  'completed-at': string;
  'created-at': string;
  'creator-id': Creator['id'];
  'creator-type': Creator['type'];
  'errored-at': string;
  'error-cause': string;
  'error-message': string;
  'graph-id': string;
  'query-hint': GraphQueryHint | null;
  params: GraphQueryParams;
  'redacted-at': string | null;
  result?: ServerAccountsResponse | ServerClusterResponse | ServerNHopsResponse;
  'result-url'?: string | null;
  'timed-out-at': string | null;
  'updated-at': string;
}

export interface GraphQueryRelationships {
  'graph-query-template': ApiOptionalRelationship<ApiModel.GraphQueryTemplate>;
  'oldest-snapshot': ApiOptionalRelationship<ApiModel.GraphSnapshot>;
}

export type GraphQuery = ApiResource<
  GraphQueryAttributes,
  GraphQueryRelationships,
  ApiModel.GraphQuery
>;

// ------------------------------------
// Explorer V2 datatypes here...
// ------------------------------------
// mirrors GraphQuery::Conditional::Operator in the backend
export enum Operator {
  Equals = 'equals',
  Matches = 'matches',
  Includes = 'includes',
  IncludesAllOf = 'includes-all-of',
  IncludesAnyOf = 'includes-any-of',
  Between = 'between',
  Before = 'before',
  After = 'after',
  LessThan = 'less-than',
  GreaterThan = 'greater-than',
  PartiallyMatches = 'partially-matches',
  IsAnyOf = 'is-any-of',
  IsNotNull = 'is-not-null',
  IsNull = 'is-null',
  Within = 'within',
}

// Keep in sync with GraphQuery::Conditional::ValueType
export enum ValueType {
  Date = 'date',
  DateTime = 'datetime',
  Number = 'number',
  Text = 'text',
  Range = 'range',
}

interface BaseValue {
  type: ValueType;
  'raw-value': string;
  errored?: boolean;
  variable?: boolean;
  // the name for this value (used for variables)
  name?: string;
  value?: string | number | [number, number] | [string, string];
}

// HAX: Used for form field names. We should probably decouple the form type system
// from the backend query types.
export enum LeftConstants {
  ByClusterCreatedAt = 'all_accounts_created_at',
}

// ------------------------------------------------------------------------
// Text conditionals
// ------------------------------------------------------------------------

export interface TextValue extends BaseValue {
  type: ValueType.Text;
  'raw-value': string;
  value: string;
}

export interface TextEqualsPredicate {
  operator: Operator.Equals;
  right: [TextValue];
}

export interface TextMatchesPredicate {
  operator: Operator.Matches;
  right: [TextValue];
}

export interface TextPartiallyMatchesPredicate {
  operator: Operator.PartiallyMatches;
  right: [TextValue];
}

export type TextPredicate =
  | TextEqualsPredicate
  | TextMatchesPredicate
  | TextPartiallyMatchesPredicate;

export type TextConditional = {
  // Note: The left options are kind of a lie in the case of custom datum types.
  left: 'account' | 'reference_id' | 'email' | 'name' | 'government_id';
} & TextPredicate;

export interface TextSelectionPredicate {
  operator: Operator.IsAnyOf;
  right: TextValue[];
}

export type AccountStatusConditional = {
  left: 'account_status';
} & (TextPredicate | TextSelectionPredicate);

// ------------------------------------------------------------------------
// Date conditionals
// ------------------------------------------------------------------------

export interface DateValue extends BaseValue {
  type: ValueType.Date;
  'raw-value': string;
  value: string; // ISO8601 string
  moment: string; // ISO8601 string
}

export interface DateEqualsPredicate {
  operator: Operator.Equals;
  right: [DateValue];
}

export interface DateBeforePredicate {
  operator: Operator.Before;
  right: [DateValue];
}

export interface DateAfterPredicate {
  operator: Operator.After;
  right: [DateValue];
}

export interface DateBetweenPredicate {
  operator: Operator.Between;
  right: [[DateValue, DateValue]];
}

export type DatePredicate =
  | DateEqualsPredicate
  | DateBeforePredicate
  | DateAfterPredicate
  | DateBetweenPredicate;

export type ByClusterCreatedAtConditional = {
  left: LeftConstants.ByClusterCreatedAt;
} & (DatePredicate | DateTimeWithinPredicate);

export type DateConditional = {
  left: 'created_at' | 'birthdate' | LeftConstants.ByClusterCreatedAt;
} & (DatePredicate | DateTimeWithinPredicate);

// ------------------------------------------------------------------------
// DateTime conditionals
// ------------------------------------------------------------------------

export interface DateTimeValue extends BaseValue {
  type: ValueType.DateTime;
  'raw-value': string;
  value: string; // ISO8601 string
  moment: string; // ISO8601 string
}

export interface DateTimeEqualsPredicate {
  operator: Operator.Equals;
  right: [DateTimeValue];
}

export interface DateTimeBeforePredicate {
  operator: Operator.Before;
  right: [DateTimeValue];
}

export interface DateTimeAfterPredicate {
  operator: Operator.After;
  right: [DateTimeValue];
}

export interface DateTimeBetweenPredicate {
  operator: Operator.Between;
  right: [[DateTimeValue, DateTimeValue]];
}

export interface DateTimeWithinPredicate {
  operator: Operator.Within;
  right: [RangeValue];
}

export type DateTimePredicate =
  | DateTimeEqualsPredicate
  | DateTimeBeforePredicate
  | DateTimeAfterPredicate
  | DateTimeBetweenPredicate
  | DateTimeWithinPredicate;

export type DateTimeConditional = {
  left: never;
} & DatePredicate;

// ------------------------------------------------------------------------
// Range conditionals
// ------------------------------------------------------------------------

export type TimeUnitOptions = 'ms' | 's' | 'm' | 'h' | 'd' | 'w' | 'mo' | 'y';

export interface RangeValue extends BaseValue {
  type: ValueType.Range;
  'raw-value': string;
  value: string;
  'parsed-relative-time': {
    periods: number;
    unit: TimeUnitOptions;
  };
  'range-value-type': ValueType.DateTime | ValueType.Date;
  // gets computed at run time
  range: {
    lower: DateTimeValue | DateValue;
    upper: DateTimeValue | DateValue;
  } | null;
}

// ------------------------------------------------------------------------
// Number conditionals
// ------------------------------------------------------------------------

export interface NumberValue extends BaseValue {
  type: ValueType.Number;
  'raw-value': string;
  value: number;
}

export interface NumberEqualsPredicate {
  operator: Operator.Equals;
  right: [NumberValue];
}

export interface NumberLessThanPredicate {
  operator: Operator.LessThan;
  right: [NumberValue];
}

export interface NumberGreaterThanPredicate {
  operator: Operator.GreaterThan;
  right: [NumberValue];
}

export type NumberPredicate =
  | NumberEqualsPredicate
  | NumberLessThanPredicate
  | NumberGreaterThanPredicate;

export type NumberConditional = {
  left: never;
} & NumberPredicate;

// NOTE: We don't handle ranges as a separate conditional type, because
// they only show up as right values, not left values for now.

export type ValueItem = TextValue | NumberValue | DateValue | DateTimeValue | RangeValue;

// ------------------------------------------------------------------------
// Valueless conditionals
// ------------------------------------------------------------------------

export interface ValuelessIsNullPredicate {
  operator: Operator.IsNull;
  right: [];
}

export interface ValuelessIsNotNullPredicate {
  operator: Operator.IsNotNull;
  right: [];
}

export type ValuelessPredicate = ValuelessIsNullPredicate | ValuelessIsNotNullPredicate;

export type ValuelessConditional = {
  left:
    | 'name'
    | 'birthdate'
    | 'government_id'
    | 'social_security_number'
    | 'email'
    | 'device_fingerprint'
    | 'browser_fingerprint'
    | 'device_vendor_id'
    | 'ip_address'
    | 'phone_number'
    | 'postal_code';
} & ValuelessPredicate;

// ------------------------------------------------------------------------
// Searchable field conditionals
// ------------------------------------------------------------------------
export interface SearchableFieldEqualsPredicate {
  operator: Operator.Equals;
  right: [TextValue];
}
export type SearchableFieldPredicate = SearchableFieldEqualsPredicate;

export type SearchableFieldConditional = {
  left: `field:acttp_${string}:${string}`;
} & SearchableFieldPredicate;

// ------------------------------------------------------------------------
// Shared properties conditionals
// ------------------------------------------------------------------------

export interface SharedPropertiesAnyOfPredicate {
  operator: Operator.IncludesAnyOf;
  right: TextValue[];
}

export interface SharedPropertiesAllOfPredicate {
  operator: Operator.IncludesAllOf;
  right: TextValue[];
}

export type SharedPropertiesPredicate =
  | SharedPropertiesAnyOfPredicate
  | SharedPropertiesAllOfPredicate;

export type SharedPropertiesConditional = {
  left: 'shared_properties';
} & SharedPropertiesPredicate;

export interface ConditionalsPayload {
  conditionals: Conditional[];
  'traversal-conditionals'?: Conditional[];
  variables: VariableValue[];
}

export enum QueryType {
  ByCluster = 'by_cluster',
  ByAccount = 'by_account',
  NHopsFromAccount = 'n_hops_from_account',
  ExperimentalLiveQuery = 'experimental_live_query',
}

export interface ClustersQuery extends ConditionalsPayload {
  type: QueryType.ByCluster;
  'min-cluster-size': number;
  'num-hops': number;
  'known-node-ids': NodeId[];
  'node-limit': number;
  'expandability-limit': number;
  'start-cursor': ServerQueryCursor;
}

export interface AccountsQuery extends ConditionalsPayload {
  type: QueryType.ByAccount;
  'known-node-ids': NodeId[];
  'node-limit': number;
  'expandability-limit': number;
  'start-cursor': ServerQueryCursor;
}

export interface NHopsFromAccountQuery extends ConditionalsPayload {
  type: QueryType.NHopsFromAccount;
  'num-hops': number;
  'known-node-ids': NodeId[];
  'node-limit': number;
  'expandability-limit': number;
  'start-cursor': ServerQueryCursor;
}

export interface ExperimentalLiveQuery {
  type: QueryType.ExperimentalLiveQuery;
}

type V2Query = AccountsQuery | ClustersQuery | NHopsFromAccountQuery;

// The server representation of query objects uses snake case for keys, whereas the frontend uses kebab case.
const kebabKeys = (obj: V2Query): V2Query => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return Object.keys(obj).reduce(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (result, key: keyof V2Query) => ({
      ...result,
      [kebabCase(key)]: obj[key],
    }),
    {},
  ) as V2Query;
};

const isSearchableFieldConditional = (conditional: Conditional): boolean => {
  if (/^field:acttp_[^:]+:.*$/.test(conditional.left)) {
    // A searchable field is okay too.
    return true;
  }
  return false;
};

// rails saves many fields as strings, so we need to convert some fields back
export const transformValueFromRails = function <Type extends ValueItem>(value: Type): Type {
  let errored = value.errored;
  // Rails stores errored as "t" and "f"
  if (typeof value.errored === 'string') {
    errored = value.errored === 't';
  }

  let variable = value.variable;
  // Rails stores variable as "t" and "f"
  if (typeof value.variable === 'string') {
    variable = value.variable === 't';
  }

  // Convert the moment values for datetime and date
  let momentValue: DateTime;
  switch (value.type) {
    case ValueType.Date: {
      if (typeof value.moment === 'string') {
        momentValue = DateTime.fromISO(value.moment).toUTC().startOf('day');
      }
      break;
    }
    case ValueType.DateTime: {
      if (typeof value.moment === 'string') {
        momentValue = DateTime.fromISO(value.moment).toUTC();
      }
      break;
    }
  }

  switch (value.type) {
    case ValueType.Date:
    case ValueType.DateTime:
      return {
        ...value,
        errored,
        variable,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        moment: momentValue ? momentValue.toISO() : undefined,
      };
    case ValueType.Range:
      return {
        ...value,
        errored,
        variable,
        ...(value['range']
          ? {
              range: {
                ...value['range'],
                lower: transformValueFromRails(value['range']['lower']),
                upper: transformValueFromRails(value['range']['upper']),
              },
            }
          : {}),
      };
    default:
      return {
        ...value,
        errored,
        variable,
      };
  }
};

// (modifies the input conditional accordingly)
export const transformConditionalFromRails = (conditional: Conditional): Conditional => {
  switch (conditional.left) {
    case 'account':
    case 'reference_id': {
      // TextConditional
      switch (conditional.operator) {
        case Operator.Equals:
        case Operator.Matches:
        case Operator.PartiallyMatches: {
          if (conditional.right.length !== 1) {
            // Bad production data -> we'll ignore.
            captureMessage(
              `Invalid conditional.right value for (left, operator): (${conditional.left}, ${conditional.operator})`,
            );
            return { ...conditional };
          }
          const [rightValue] = conditional.right;
          return {
            ...conditional,
            right: [transformValueFromRails(rightValue)],
          };
        }
        default:
          return assertNever(conditional);
      }
    }
    case 'account_status':
      switch (conditional.operator) {
        case Operator.Equals:
        case Operator.Matches:
        case Operator.PartiallyMatches: {
          if (conditional.right.length !== 1) {
            // Bad production data -> we'll ignore.
            captureMessage(
              `Invalid conditional.right value for (left, operator): (${conditional.left}, ${conditional.operator})`,
            );
            return { ...conditional };
          }
          const [rightValue] = conditional.right;
          return {
            ...conditional,
            right: [transformValueFromRails(rightValue)],
          };
        }
        case Operator.IsAnyOf: {
          return {
            ...conditional,
            right: conditional.right.map(transformValueFromRails),
          };
        }
        default: {
          return assertNever(conditional);
        }
      }
    case 'ip_address':
    case 'postal_code':
    case 'phone_number':
    case 'device_fingerprint':
    case 'browser_fingerprint':
    case 'device_vendor_id':
    case 'name':
    case 'government_id':
    case 'social_security_number':
    case 'email': {
      switch (conditional.operator) {
        case Operator.Equals:
        case Operator.Matches:
        case Operator.PartiallyMatches: {
          // TextConditional
          if (conditional.right.length !== 1) {
            // Bad production data -> we'll ignore.
            captureMessage(
              `Invalid conditional.right value for (left, operator): (${conditional.left}, ${conditional.operator})`,
            );
            return { ...conditional };
          }
          const [rightValue] = conditional.right;
          return {
            ...conditional,
            right: [transformValueFromRails(rightValue)],
          };
        }
        case Operator.IsNull:
        case Operator.IsNotNull: {
          return { ...conditional };
        }
        default: {
          return assertNever(conditional);
        }
      }
    }
    case 'created_at':
    case 'birthdate': {
      switch (conditional.operator) {
        case Operator.Equals:
        case Operator.Before:
        case Operator.After: {
          // DateConditional
          if (conditional.right.length !== 1) {
            // Bad production data -> we'll ignore.
            captureMessage(
              `Invalid conditional.right value for (left, operator): (${conditional.left}, ${conditional.operator})`,
            );
            return { ...conditional };
          }
          const [rightValue] = conditional.right;
          return {
            ...conditional,
            right: [transformValueFromRails(rightValue)],
          };
        }
        case Operator.Between: {
          // DateConditional
          if (conditional.right.length !== 1 || conditional.right[0].length !== 2) {
            // Bad production data -> we'll ignore.
            captureMessage(
              `Invalid conditional.right value for (left, operator): (${conditional.left}, ${conditional.operator})`,
            );
            return { ...conditional };
          }
          const [[startValue, endValue]] = conditional.right;
          return {
            ...conditional,
            right: [[transformValueFromRails(startValue), transformValueFromRails(endValue)]],
          };
        }
        case Operator.Within:
          if (conditional.right.length !== 1) {
            captureMessage(
              `Invalid conditional.right value for (left, operator): (${conditional.left}, ${conditional.operator})`,
            );
            return { ...conditional };
          }
          return {
            ...conditional,
            right: [transformValueFromRails(conditional.right[0])],
          };
        case Operator.IsNull:
        case Operator.IsNotNull:
          return { ...conditional };
        default:
          return assertNever(conditional);
      }
    }
    case 'shared_properties': {
      // SharedPropertiesConditional
      switch (conditional.operator) {
        case Operator.IncludesAnyOf:
        case Operator.IncludesAllOf: {
          return {
            ...conditional,
            right: conditional.right.map(transformValueFromRails),
          };
        }
        default: {
          // it is not possible for a conditional to be an object
          // for this default case, so enforce with assertNever
          return assertNever(conditional);
        }
      }
    }
    case LeftConstants.ByClusterCreatedAt:
      return { ...conditional };
    default: {
      if (isSearchableFieldConditional(conditional)) {
        // A searchable field conditional is okay too.
        const rightValue = conditional.right;
        if (rightValue.length !== 1) {
          // Bad production data -> we'll ignore.
          captureMessage(
            `Invalid conditional.right value for (left, operator): (${conditional.left}, ${conditional.operator})`,
          );
          return { ...conditional };
        }
        return {
          ...conditional,
          right: [transformValueFromRails(rightValue[0])],
        };
      }
      captureMessage(`Invalid conditional.left value: ${conditional.left}`);
      return { ...conditional };
    }
  }
};

export const isConditionalValid = (conditional: Conditional): boolean => {
  switch (conditional.left) {
    case 'account':
    case 'reference_id': {
      // TextConditional
      switch (conditional.operator) {
        case Operator.Equals: {
          if (conditional.right.length !== 1) {
            return false;
          }

          const [rightValue]: [TextValue] = conditional.right;
          if (rightValue.errored) {
            return false;
          }

          return true;
        }
        case Operator.Matches:
        case Operator.PartiallyMatches: {
          return true;
        }
        default:
          return assertNever(conditional);
      }
    }
    case 'ip_address':
    case 'postal_code':
    case 'phone_number':
    case 'device_fingerprint':
    case 'browser_fingerprint':
    case 'device_vendor_id':
    case 'email':
    case 'government_id':
    case 'social_security_number':
    case 'name': {
      switch (conditional.operator) {
        case Operator.Equals:
        case Operator.Matches:
        case Operator.PartiallyMatches: {
          // TextConditional
          if (conditional.right.length !== 1) {
            return false;
          }
          const [rightValue]: [TextValue] = conditional.right;
          if (rightValue.errored) {
            return false;
          }

          return true;
        }
        case Operator.IsNull:
        case Operator.IsNotNull: {
          if (conditional.right.length !== 0) {
            return false;
          }

          return true;
        }
        default:
          return assertNever(conditional);
      }
    }
    case 'created_at':
    case 'birthdate': {
      // DateConditional
      switch (conditional.operator) {
        case Operator.Equals:
        case Operator.Before:
        case Operator.After: {
          if (conditional.right.length !== 1) {
            return false;
          }
          const [rightValue]: [DateValue] = conditional.right;
          if (rightValue.errored) {
            return false;
          }

          return true;
        }
        case Operator.Between: {
          if (conditional.right.length !== 1 || conditional.right[0].length !== 2) {
            return false;
          }

          const [[startValue, endValue]]: [[DateValue, DateValue]] = conditional.right;
          if (startValue.errored || endValue.errored) {
            return false;
          }

          return true;
        }
        case Operator.Within: {
          if (conditional.right.length !== 1) {
            return false;
          }
          const [rightValue]: [RangeValue] = conditional.right;
          return !rightValue.errored;
        }
        case Operator.IsNull:
        case Operator.IsNotNull: {
          return true;
        }
        default:
          return assertNever(conditional);
      }
    }
    case 'shared_properties': {
      // SharedPropertiesConditional
      switch (conditional.operator) {
        case Operator.IncludesAnyOf:
        case Operator.IncludesAllOf: {
          if (conditional.right.length === 0) {
            return false;
          }
          const rightValues: TextValue[] = conditional.right;
          if (rightValues.some((textValue) => textValue.errored)) {
            return false;
          }

          return true;
        }
        default:
          return assertNever(conditional);
      }
    }
    case 'account_status':
    case LeftConstants.ByClusterCreatedAt:
      return true;
    // TODO(kerry): Add left values for DateTimeConditional and NumberConditional, if we add them in the future.
    default:
      if (isSearchableFieldConditional(conditional)) {
        // A searchable field conditional is okay too.
        return true;
      }
      return false;
  }

  return true;
};

const isEmptyValue = (value: BaseValue['value']) => {
  switch (typeof value) {
    case 'number':
      return false;
    default:
      return isEmpty(value);
  }
};

export const hasEmptyVariable = (conditional: Conditional): boolean => {
  const variableCheck = (val: BaseValue) => val.variable && isEmptyValue(val.value);
  switch (conditional.operator) {
    case Operator.Equals:
    case Operator.Matches:
    case Operator.PartiallyMatches:
    case Operator.Before:
    case Operator.After:
    case Operator.LessThan:
    case Operator.GreaterThan:
      // conditional.right: [TextValue] | [DateValue] | [DateTimeValue] | [NumberValue]
      return conditional.right.some(variableCheck);
    case Operator.IsAnyOf:
    case Operator.IncludesAnyOf:
    case Operator.IncludesAllOf:
      // conditional.right:  TextValue[]
      return conditional.right.some(variableCheck);
    case Operator.Between:
      // conditional.right: [[DateValue, DateValue]] | [[DateTimeValue, DateTimeValue]]
      return conditional.right.some((rightValues) => rightValues.some(variableCheck));
    case Operator.IsNull:
    case Operator.IsNotNull:
      // conditional.right: []
      return false;
    case Operator.Within:
      // TODO - update logic once variables are supported for `within` operators
      // conditional.right: [RangeValue]
      return false;
    default:
      return assertNever(conditional);
  }
};

export function sanitizePayload(payload: ConditionalsPayload) {
  const conditionals = payload.conditionals;
  const traversalConditionals = payload['traversal-conditionals'];
  const variables = payload.variables;

  let sanitizedPayload: ConditionalsPayload = { ...payload, variables };

  if (conditionals && conditionals.length > 0) {
    sanitizedPayload = {
      ...sanitizedPayload,
      conditionals: conditionals.map(transformConditionalFromRails),
    };
  }

  if (traversalConditionals && traversalConditionals.length > 0) {
    sanitizedPayload = {
      ...sanitizedPayload,
      'traversal-conditionals': traversalConditionals.map(transformConditionalFromRails),
    };
  }

  return sanitizedPayload;
}

export function sanitizeGraphQuery(graphQuery: GraphQuery): GraphQuery {
  if (
    graphQuery.attributes.params.type === QueryType.ByAccount ||
    graphQuery.attributes.params.type === QueryType.ByCluster ||
    graphQuery.attributes.params.type === QueryType.NHopsFromAccount
  ) {
    return {
      ...graphQuery,
      attributes: {
        ...graphQuery.attributes,
        params: kebabKeys(sanitizePayload(graphQuery.attributes.params) as V2Query),
      },
    };
  }
  return graphQuery;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function formValuesToConditionalsPayload(formValues: any): ConditionalsPayload {
  const keys = Object.keys(formValues);

  const conditionals = keys.map((key) => {
    return {
      left: formValues[key]['left'],
      operator: formValues[key]['operator'],
      right: formValues[key]['right'],
    };
  });

  return {
    conditionals,
    variables: [],
  };
}

export const ACCOUNT_FORM_PREFIX = 'ACCOUNT';
export const CLUSTER_FORM_PREFIX = 'CLUSTER';

export function generateFormValues(type: QueryType, conditionals: Conditional[]) {
  let prefix: string;
  switch (type) {
    case 'by_account': {
      prefix = ACCOUNT_FORM_PREFIX;
      break;
    }
    case 'by_cluster': {
      prefix = CLUSTER_FORM_PREFIX;
      break;
    }
  }

  return conditionals.reduce(
    (memo: Record<string, Conditional>, conditional: Conditional, i: number) => {
      return {
        ...memo,
        [`${prefix}-${i}`]: conditional,
      };
    },
    {},
  );
}

export function graphQueryToFormValues(graphQuery: GraphQuery): Record<string, Conditional> {
  const params: AccountsQuery = graphQuery.attributes.params as AccountsQuery;
  return generateFormValues(params.type, params.conditionals);
}

export type Conditional =
  | TextConditional
  | DateConditional
  | DateTimeConditional
  | NumberConditional
  | ValuelessConditional
  | SearchableFieldConditional
  | SharedPropertiesConditional
  | AccountStatusConditional;

export interface VariableValue {
  name: string;
  type: string;
  value: string;
}

export interface ParameterMap {
  [key: string]: string;
}

export function generateVariableMap(arr: VariableValue[]): ParameterMap {
  const obj: ParameterMap = {};
  for (const variable of arr) {
    obj[variable.name] = variable.value;
  }
  return obj;
}

export type GraphQueryBody = ClustersQuery | AccountsQuery | NHopsFromAccountQuery;

// Form modeling
export interface QueryConfigArgs {
  accountTypes: AccountType[];
  accountStatuses: AccountStatus[];
  datumTypes: DatumType[];
  customDatumTypes: DatumType[];
  propertyDatumTypes: DatumType[];
  graphPropertyConfigs: PropertyConfig[];
}

// Enums for the items that go on the left side of a conditional.
export type QueryConditionalLeft =
  | DatumType['name']
  | 'reference_id'
  | 'created_at'
  | 'shared_properties';

export interface QueryConditionalConfig {
  left: {
    key: QueryConditionalLeft;
    display: string;
  };
  operatorToRight: Partial<Record<Operator, ControlConfig>>;
  placeHolder?: string;
}

export type QueryConfig = QueryConditionalConfig[];

// Maps datum value type to operator.
const DatumValueTypeToOperator = (
  valueType: DatumValueType,
): Partial<Record<Operator, ControlConfig>> => {
  switch (valueType) {
    case DatumValueType.Boolean: {
      return {
        [Operator.Equals]: {
          type: Control.Select,
          multi: false,
          options: [
            { label: 'True', value: 'true' },
            { label: 'False', value: 'false' },
          ],
        },
      };
    }
    case DatumValueType.Decimal:
    case DatumValueType.Integer: {
      return {
        [Operator.Equals]: {
          type: Control.NumberPicker,
          multi: false,
        },
        [Operator.LessThan]: {
          type: Control.NumberPicker,
          multi: false,
        },
        [Operator.GreaterThan]: {
          type: Control.NumberPicker,
          multi: false,
        },
      };
    }
    case DatumValueType.String: {
      return {
        [Operator.Equals]: {
          type: Control.Input,
        },
        [Operator.PartiallyMatches]: {
          type: Control.Input,
        },
      };
    }
    default: {
      return {
        [Operator.Equals]: {
          type: Control.Input,
          multi: false,
        },
      };
    }
  }
};

// Maps property value type to operator.
const PropertyValueTypeToOperator = (
  valueType: GraphPropertyType,
): Partial<Record<Operator, ControlConfig>> => {
  switch (valueType) {
    case GraphPropertyType.Boolean: {
      return {
        [Operator.Equals]: {
          type: Control.Select,
          multi: false,
          options: [
            { label: 'True', value: 'true' },
            { label: 'False', value: 'false' },
          ],
        },
      };
    }
    case GraphPropertyType.Decimal:
    case GraphPropertyType.Integer: {
      return {
        [Operator.Equals]: {
          type: Control.NumberPicker,
          multi: false,
        },
        [Operator.LessThan]: {
          type: Control.NumberPicker,
          multi: false,
        },
        [Operator.GreaterThan]: {
          type: Control.NumberPicker,
          multi: false,
        },
      };
    }
    default: {
      return {
        [Operator.Equals]: {
          type: Control.Input,
          multi: false,
        },
      };
    }
  }
};

export const buildByAccountQueryConfig = (
  { customDatumTypes, graphPropertyConfigs }: Partial<QueryConfigArgs>,
  getNodeRenderConfig: NodeRenderConfigLookupFn,
  transactionsEnabled: boolean,
): QueryConfig => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  let customDataTypeOptions: QueryConditionalConfig[] = customDatumTypes.map((datumType) => {
    return {
      left: {
        key: datumType.token,
        display: getNodeRenderConfig(datumType.token).displayName,
      },
      operatorToRight: DatumValueTypeToOperator(datumType.valueType),
    };
  });

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  let propertyOptions: QueryConditionalConfig[] = graphPropertyConfigs.reduce(
    (result, propertyConfig) => {
      if (propertyConfig.indexed) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        result.push({
          left: {
            key: [propertyConfig.token, propertyConfig.source, propertyConfig.value].join(':'),
            display: propertyConfig.displayName,
          },
          operatorToRight: PropertyValueTypeToOperator(propertyConfig.type),
        });
      }
      return result;
    },
    [],
  );

  customDataTypeOptions = [];
  propertyOptions = [];

  let transactionsOptions: QueryConditionalConfig[];

  if (transactionsEnabled) {
    transactionsOptions = [
      {
        left: {
          key: 'transaction_id',
          display: 'Transaction ID',
        },
        operatorToRight: {
          [Operator.Equals]: {
            type: Control.Input,
            multi: false,
          },
        },
        placeHolder: TRANSACTION_ID_SEARCH_PLACEHOLDER,
      },
    ];
  } else {
    transactionsOptions = [];
  }

  return [
    {
      left: {
        key: ACCOUNT_DATUM_TYPE.name,
        display: 'Account ID',
      },
      operatorToRight: {
        [Operator.Equals]: {
          type: Control.Input,
          multi: false,
        },
      },
      placeHolder: DEFAULT_ACCOUNT_DATUM_TYPE_RENDER_CONFIG.placeHolder,
    },
    {
      left: {
        key: 'reference_id',
        display: 'Account Reference ID',
      },
      operatorToRight: {
        [Operator.Equals]: {
          type: Control.Input,
          multi: false,
        },
      },
      placeHolder: ACCOUNT_REFERENCE_ID_SEARCH_PLACEHOLDER,
    },
    {
      left: {
        key: BIRTHDATE_DATUM_TYPE.name,
        display: 'Birthdate',
      },
      operatorToRight: {
        [Operator.Equals]: {
          type: Control.DatePicker,
        },
        [Operator.After]: {
          type: Control.DatePicker,
        },
        [Operator.Before]: {
          type: Control.DatePicker,
        },
        [Operator.Between]: {
          type: Control.DateTimeRangePicker,
          fixTime: true,
        },
        [Operator.Within]: {
          type: Control.TimeFramePicker,
          rangeValueType: ValueType.Date,
        },
      },
      placeHolder: getNodeRenderConfig(BIRTHDATE_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: NAME_DATUM_TYPE.name,
        display: 'Name',
      },
      operatorToRight: {
        [Operator.Matches]: {
          type: Control.Input,
        },
        [Operator.PartiallyMatches]: {
          type: Control.Input,
        },
      },
      placeHolder: getNodeRenderConfig(NAME_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: BROWSER_FINGERPRINT_DATUM_TYPE.name,
        display: 'Browser fingerprint',
      },
      operatorToRight: {
        [Operator.Equals]: { type: Control.Input },
      },
      placeHolder: getNodeRenderConfig(BROWSER_FINGERPRINT_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.name,
        display: 'Device fingerprint (Browser)',
      },
      operatorToRight: {
        [Operator.Equals]: { type: Control.Input },
      },
      placeHolder: getNodeRenderConfig(DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.name,
        display: 'Device fingerprint (Mobile SDK)',
      },
      operatorToRight: {
        [Operator.Equals]: { type: Control.Input },
      },
      placeHolder: getNodeRenderConfig(DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: EMAIL_DATUM_TYPE.name,
        display: 'Email Address',
      },
      operatorToRight: {
        [Operator.Equals]: { type: Control.Input },
      },
      placeHolder: getNodeRenderConfig(EMAIL_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: GOVERNMENT_ID_DATUM_TYPE.name,
        display: 'Government ID Number',
      },
      operatorToRight: {
        [Operator.Equals]: { type: Control.Input },
      },
      placeHolder: getNodeRenderConfig(GOVERNMENT_ID_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: SOCIAL_SECURITY_NUMBER_DATUM_TYPE.name,
        display: 'SSN',
      },
      operatorToRight: {
        [Operator.Equals]: { type: Control.Input },
      },
      placeHolder: getNodeRenderConfig(SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: PHONE_NUMBER_DATUM_TYPE.name,
        display: 'Phone Number',
      },
      operatorToRight: {
        [Operator.Equals]: { type: Control.Input },
      },
      placeHolder: getNodeRenderConfig(PHONE_NUMBER_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: IP_ADDRESS_NAME,
        display: 'IP Address',
      },
      operatorToRight: {
        [Operator.Equals]: { type: Control.Input },
      },
      placeHolder: getNodeRenderConfig(NETWORK_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: POSTAL_CODE_DATUM_TYPE.name,
        display: 'Postal Code',
      },
      operatorToRight: {
        [Operator.Equals]: { type: Control.Input },
      },
      placeHolder: getNodeRenderConfig(POSTAL_CODE_DATUM_TYPE.token).placeHolder,
    },
    ...transactionsOptions,
    ...customDataTypeOptions,
    ...propertyOptions,
    {
      left: {
        key: 'created_at',
        display: 'Created at',
      },
      operatorToRight: {
        [Operator.After]: { type: Control.DateTimePicker },
        [Operator.Before]: { type: Control.DateTimePicker },
        [Operator.Between]: {
          type: Control.DateTimeRangePicker,
          fixTime: true,
        },
        [Operator.Within]: {
          type: Control.TimeFramePicker,
          rangeValueType: ValueType.DateTime,
        },
      },
    },
  ];
};

export const buildByClusterQueryConfig = (
  queryConfigArgs: QueryConfigArgs,
  getNodeRenderConfig: NodeRenderConfigLookupFn,
): QueryConfig => {
  const nodeDatumTypeOptions = queryConfigArgs.customDatumTypes.map((datumType) => {
    return {
      label: getNodeRenderConfig(datumType.token).displayName,
      value: datumType.token,
    };
  });

  const sharedPropertiesOptions = ({ propertyDatumTypes }: QueryConfigArgs) => {
    const sharedProperties = propertyDatumTypes || [];
    return sharedProperties.map((datumType) => ({
      label: getNodeRenderConfig(datumType.token).displayName,
      value: datumType.token,
    }));
  };

  return [
    {
      left: {
        key: 'shared_properties',
        display: 'Shared properties',
      },
      operatorToRight: {
        [Operator.IncludesAnyOf]: {
          type: Control.Select,
          multi: true,
          options: [...sharedPropertiesOptions(queryConfigArgs), ...nodeDatumTypeOptions],
        },
        [Operator.IncludesAllOf]: {
          type: Control.Select,
          multi: true,
          options: [...sharedPropertiesOptions(queryConfigArgs), ...nodeDatumTypeOptions],
        },
      },
    },
    {
      left: {
        key: 'created_at',
        display: 'Accounts created at',
      },
      operatorToRight: {
        [Operator.Between]: {
          type: Control.DateTimeRangePicker,
          fixTime: true,
        },
        [Operator.Within]: {
          type: Control.TimeFramePicker,
          rangeValueType: ValueType.DateTime,
        },
      },
    },
  ];
};

export const buildTraversalFilteringConfig = (
  { accountStatuses }: Partial<QueryConfigArgs>,
  getNodeRenderConfig: NodeRenderConfigLookupFn,
): QueryConfig => {
  return [
    {
      left: {
        key: NAME_DATUM_TYPE.name,
        display: 'Name',
      },
      operatorToRight: {
        [Operator.IsNotNull]: {
          type: Control.None,
        },
        [Operator.IsNull]: {
          type: Control.None,
        },
      },
      placeHolder: getNodeRenderConfig(NAME_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: BIRTHDATE_DATUM_TYPE.name,
        display: 'Birthdate',
      },
      operatorToRight: {
        [Operator.IsNotNull]: {
          type: Control.None,
        },
        [Operator.IsNull]: {
          type: Control.None,
        },
        [Operator.After]: {
          type: Control.DatePicker,
        },
        [Operator.Before]: {
          type: Control.DatePicker,
        },
        [Operator.Within]: {
          type: Control.TimeFramePicker,
          rangeValueType: ValueType.Date,
        },
      },
      placeHolder: getNodeRenderConfig(BIRTHDATE_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: GOVERNMENT_ID_DATUM_TYPE.name,
        display: 'Government ID',
      },
      operatorToRight: {
        [Operator.IsNotNull]: {
          type: Control.None,
        },
        [Operator.IsNull]: {
          type: Control.None,
        },
      },
      placeHolder: getNodeRenderConfig(GOVERNMENT_ID_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: SOCIAL_SECURITY_NUMBER_DATUM_TYPE.name,
        display: 'SSN',
      },
      operatorToRight: {
        [Operator.IsNotNull]: {
          type: Control.None,
        },
        [Operator.IsNull]: {
          type: Control.None,
        },
      },
      placeHolder: getNodeRenderConfig(SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: EMAIL_DATUM_TYPE.name,
        display: 'Email address',
      },
      operatorToRight: {
        [Operator.IsNotNull]: {
          type: Control.None,
        },
        [Operator.IsNull]: {
          type: Control.None,
        },
      },
      placeHolder: getNodeRenderConfig(EMAIL_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: ACCOUNT_STATUS_DATUM_TYPE.name,
        display: 'Account status',
      },
      operatorToRight: {
        [Operator.IsAnyOf]: {
          type: Control.Select,
          multi: true,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          options: accountStatuses.map((accountStatus) => {
            return {
              label: accountStatus.attributes.name,
              value: accountStatus.id,
            };
          }),
        },
      },
      placeHolder: getNodeRenderConfig(ACCOUNT_STATUS_DATUM_TYPE.token).placeHolder,
    },
    {
      left: {
        key: 'created_at',
        display: 'Created at',
      },
      operatorToRight: {
        [Operator.After]: { type: Control.DateTimePicker },
        [Operator.Before]: { type: Control.DateTimePicker },
        [Operator.Between]: {
          type: Control.DateTimeRangePicker,
          fixTime: true,
        },
        [Operator.Within]: {
          type: Control.TimeFramePicker,
          rangeValueType: ValueType.DateTime,
        },
      },
    },
  ];
};

export const hasTraversalFiltering = (graphQuery: GraphQuery): boolean => {
  switch (graphQuery?.attributes?.params?.type) {
    case 'n_hops_from_account': {
      const traversalConditionalLength =
        graphQuery.attributes.params['traversal-conditionals']?.length ?? 0;
      return traversalConditionalLength > 0;
    }
    default:
      return false;
  }
};

// Convert graphQuery response to nodes and edges
export function graphQueryResponseToV2Data(response?: GraphQuery['attributes']['result']): V2Data {
  const nodes: Map<HashableNodeId, Node> = new Map();
  const edges: Edge[] = [];
  const seedNodes: Node[] = [];
  const nonfetchedEdgeCounts: Map<HashableNodeId, number> = new Map();

  if (response) {
    response.nodes?.forEach((serverNode) => {
      const nodeId = convertServerNodeToNodeId(serverNode);
      nodes.set(nodeIdToHashable(nodeId), convertServerNodeToNode(serverNode));
    });
    response.edges?.forEach((serverEdge) => {
      edges.push(convertServerEdgeToEdge(serverEdge));
    });

    if ('seed-set' in response) {
      const seedSetHashables = response['seed-set'].map((seedNode) => {
        const nodeId = convertServerNodeToNodeId(seedNode);
        const hashableNodeId = nodeIdToHashable(nodeId);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        seedNodes.push(nodes.get(hashableNodeId));
        return hashableNodeId;
      });

      for (const [nodeHashable, node] of nodes.entries()) {
        if (seedSetHashables.includes(nodeHashable)) {
          node.state.featured = true;
        }
      }
    }

    response['expandable-nodes'].forEach((serverNodeExpandability) => {
      const nodeId = convertServerNodeToNodeId(serverNodeExpandability.node);
      const hashableNodeId = nodeIdToHashable(nodeId);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      nodes.get(hashableNodeId).state.nonfetchedEdgeCount =
        serverNodeExpandability['nonfetched-edge-count'];
      nonfetchedEdgeCounts.set(hashableNodeId, serverNodeExpandability['nonfetched-edge-count']);
    });
  }

  return {
    edges,
    nodes: Array.from(nodes.values()),
    nonfetchedEdgeCounts,
    seedNodes,
  };
}
